import { useState } from "react";
import SEOHead from "@/components/feature/SEOHead";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { brandInfo, reputation, faqs, contactInfo } from "@/mocks/about";

function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <i
          key={star}
          className={`text-sm ${
            star <= rating
              ? "ri-star-fill text-accent-500"
              : "ri-star-line text-background-300"
          }`}
        ></i>
      ))}
    </div>
  );
}

export default function AboutPage() {
  const [formState, setFormState] = useState<"idle" | "success" | "error">(
    "idle"
  );
  const [formError, setFormError] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get("phone_alt") as string;
    if (honeypot && honeypot.trim() !== "") {
      setFormState("success");
      return;
    }
    formData.delete("phone_alt");

    try {
      const response = await fetch(
        "https://readdy.ai/api/form/d9nh1ou8mbnljin5a0j0",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: new URLSearchParams(
            Array.from(formData.entries()).map(([key, value]) => [
              key,
              value as string,
            ])
          ).toString(),
        }
      );

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (
        response.ok &&
        parsed &&
        parsed.code === "OK"
      ) {
        setFormState("success");
        form.reset();
      } else {
        const serverMsg =
          parsed?.meta?.message ||
          parsed?.message ||
          parsed?.meta?.detail ||
          responseText ||
          "提交失败，请稍后重试";
        setFormError(serverMsg);
        setFormState("error");
      }
    } catch {
      setFormError("网络错误，请检查网络连接后重试");
      setFormState("error");
    }
  };

  const orgSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "6789影视",
    url: "https://6789ys.com/",
    description: brandInfo.description.slice(0, 200),
    foundingDate: "2020",
    contactPoint: {
      "@type": "ContactPoint",
      email: contactInfo.email,
      contactType: "customer service",
      availableLanguage: ["Chinese"],
    },
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      <SEOHead
        title="关于我们 - 6789影视 | 品牌介绍与联系方式"
        description="6789影视成立于2020年，国内领先的综艺在线观看平台。免费高清综艺每日更新，涵盖搞笑、真人秀、音乐、脱口秀等全品类。了解我们的品牌故事与联系方式。"
        keywords="6789影视,关于我们,品牌介绍,联系方式,综艺平台"
        canonicalUrl="https://6789ys.com/about"
      />
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="w-full px-4 md:px-6 lg:px-8 max-w-7xl mx-auto py-4">
            <Breadcrumb
              items={[
                { name: "首页", path: "/" },
                { name: "关于我们" },
              ]}
            />
          </div>
        </div>

        <section className="w-full py-12 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6 lg:px-8 max-w-7xl mx-auto">
            <div className="max-w-3xl mx-auto text-center mb-14">
              <h1 className="font-heading text-3xl md:text-5xl text-foreground-950 mb-6">
                关于6789影视
              </h1>
              <p className="text-lg text-foreground-600 leading-relaxed">
                {brandInfo.mission}
              </p>
            </div>

            <div className="max-w-4xl mx-auto">
              <div className="bg-white rounded-xl p-6 md:p-10 border border-background-200/70">
                <h2 className="font-heading text-xl md:text-2xl text-foreground-950 mb-6">
                  品牌故事
                </h2>
                <div className="text-foreground-700 leading-relaxed space-y-4 text-sm md:text-base">
                  {brandInfo.description.split("\n\n").map((para, idx) => (
                    <p key={idx}>{para}</p>
                  ))}
                </div>
                <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center p-4 rounded-lg bg-background-50">
                    <div className="text-2xl font-heading text-primary-500 mb-1">
                      2020
                    </div>
                    <div className="text-xs text-foreground-500">创立年份</div>
                  </div>
                  <div className="text-center p-4 rounded-lg bg-background-50">
                    <div className="text-2xl font-heading text-primary-500 mb-1">
                      5000+
                    </div>
                    <div className="text-xs text-foreground-500">综艺节目</div>
                  </div>
                  <div className="text-center p-4 rounded-lg bg-background-50">
                    <div className="text-2xl font-heading text-primary-500 mb-1">
                      1000万+
                    </div>
                    <div className="text-xs text-foreground-500">活跃用户</div>
                  </div>
                  <div className="text-center p-4 rounded-lg bg-background-50">
                    <div className="text-2xl font-heading text-primary-500 mb-1">
                      99%
                    </div>
                    <div className="text-xs text-foreground-500">用户好评</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full py-12 md:py-16 bg-background-100">
          <div className="w-full px-4 md:px-6 lg:px-8 max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-3">
                核心价值观
              </h2>
              <p className="text-sm text-foreground-600">
                我们始终坚持的四大核心理念
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
              {brandInfo.values.map((value) => (
                <div
                  key={value.title}
                  className="bg-white rounded-xl p-6 border border-background-200/70 text-center"
                >
                  <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-50 text-primary-500">
                    <i className={`${value.icon} text-2xl`}></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-950 mb-2">
                    {value.title}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {value.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full py-12 md:py-16 bg-background-50">
          <div className="w-full px-4 md:px-6 lg:px-8 max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-3">
                用户口碑
              </h2>
              <p className="text-sm text-foreground-600">
                来自真实用户的好评与信赖
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {reputation.map((item) => (
                <div
                  key={item.name}
                  className="bg-white rounded-xl p-5 border border-background-200/70"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <img
                      src={item.avatar}
                      alt={item.name}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div>
                      <div className="text-sm font-semibold text-foreground-950">
                        {item.name}
                      </div>
                      <div className="text-xs text-foreground-500">
                        {item.date}
                      </div>
                    </div>
                  </div>
                  <StarRating rating={item.rating} />
                  <p className="mt-3 text-sm text-foreground-600 leading-relaxed">
                    {item.comment}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full py-12 md:py-16 bg-background-100">
          <div className="w-full px-4 md:px-6 lg:px-8 max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-6">
                  联系我们
                </h2>
                <p className="text-sm text-foreground-600 mb-8 leading-relaxed">
                  如果您有任何问题或建议，欢迎通过以下方式联系我们。我们的客服团队会在24小时内回复您。
                </p>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-50 text-primary-500">
                      <i className="ri-mail-line"></i>
                    </div>
                    <div>
                      <div className="text-xs text-foreground-500">邮箱</div>
                      <div className="text-sm font-medium text-foreground-950">
                        {contactInfo.email}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-50 text-accent-500">
                      <i className="ri-wechat-line"></i>
                    </div>
                    <div>
                      <div className="text-xs text-foreground-500">微信</div>
                      <div className="text-sm font-medium text-foreground-950">
                        {contactInfo.wechat}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-secondary-50 text-secondary-500">
                      <i className="ri-qq-line"></i>
                    </div>
                    <div>
                      <div className="text-xs text-foreground-500">QQ</div>
                      <div className="text-sm font-medium text-foreground-950">
                        {contactInfo.qq}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-50 text-primary-500">
                      <i className="ri-time-line"></i>
                    </div>
                    <div>
                      <div className="text-xs text-foreground-500">工作时间</div>
                      <div className="text-sm font-medium text-foreground-950">
                        {contactInfo.workingHours}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <div className="bg-white rounded-xl p-6 md:p-8 border border-background-200/70">
                  <h3 className="font-heading text-xl text-foreground-950 mb-6">
                    发送消息
                  </h3>

                  {formState === "success" ? (
                    <div className="text-center py-8">
                      <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-50 text-primary-500">
                        <i className="ri-check-line text-3xl"></i>
                      </div>
                      <h4 className="text-lg font-semibold text-foreground-950 mb-2">
                        发送成功！
                      </h4>
                      <p className="text-sm text-foreground-600">
                        感谢您的留言，我们会在24小时内回复您。
                      </p>
                      <button
                        onClick={() => setFormState("idle")}
                        className="mt-6 px-6 py-2 rounded-full bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer"
                      >
                        继续留言
                      </button>
                    </div>
                  ) : (
                    <form
                      onSubmit={handleSubmit}
                      className="space-y-4"
                      data-readdy-form=""
                    >
                      {formState === "error" && (
                        <div className="p-3 rounded-lg bg-red-50 text-red-600 text-sm">
                          {formError || "提交失败，请稍后重试"}
                        </div>
                      )}

                      <div className="contact-field-extra">
                        <input
                          type="text"
                          name="phone_alt"
                          tabIndex={-1}
                          autoComplete="off"
                          aria-hidden="true"
                          readOnly
                        />
                      </div>

                      <div>
                        <label
                          htmlFor="about-name"
                          className="block text-sm font-medium text-foreground-700 mb-1.5"
                        >
                          您的姓名
                        </label>
                        <input
                          type="text"
                          id="about-name"
                          name="name"
                          required
                          className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-400 transition-colors"
                          placeholder="请输入您的姓名"
                        />
                      </div>

                      <div>
                        <label
                          htmlFor="about-email"
                          className="block text-sm font-medium text-foreground-700 mb-1.5"
                        >
                          邮箱地址
                        </label>
                        <input
                          type="email"
                          id="about-email"
                          name="email"
                          required
                          className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-400 transition-colors"
                          placeholder="请输入您的邮箱"
                        />
                      </div>

                      <div>
                        <label
                          htmlFor="about-subject"
                          className="block text-sm font-medium text-foreground-700 mb-1.5"
                        >
                          主题
                        </label>
                        <input
                          type="text"
                          id="about-subject"
                          name="subject"
                          required
                          className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-400 transition-colors"
                          placeholder="请输入主题"
                        />
                      </div>

                      <div>
                        <label
                          htmlFor="about-message"
                          className="block text-sm font-medium text-foreground-700 mb-1.5"
                        >
                          留言内容
                        </label>
                        <textarea
                          id="about-message"
                          name="message"
                          required
                          rows={4}
                          maxLength={500}
                          className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                          placeholder="请输入您的留言内容（最多500字）"
                        ></textarea>
                        <p className="text-xs text-foreground-500 mt-1">
                          最多500字
                        </p>
                      </div>

                      <button
                        type="submit"
                        className="w-full px-6 py-3 rounded-full bg-primary-500 text-white font-medium text-sm hover:bg-primary-600 transition-colors cursor-pointer"
                      >
                        发送消息
                      </button>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full py-12 md:py-16 bg-background-50">
          <div className="w-full px-4 md:px-6 lg:px-8 max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-3">
                常见问题
              </h2>
              <p className="text-sm text-foreground-600">
                关于6789影视的常见问题解答
              </p>
            </div>

            <div className="max-w-3xl mx-auto space-y-3">
              {faqs.map((faq, index) => (
                <details
                  key={index}
                  className="group bg-white rounded-xl border border-background-200/70 overflow-hidden"
                >
                  <summary className="flex items-center justify-between px-6 py-4 cursor-pointer list-none">
                    <span className="text-sm font-medium text-foreground-950">
                      {faq.question}
                    </span>
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
                  </summary>
                  <div className="px-6 pb-4">
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}